import captionsLandingData from "./captionsLanding.json";
import type { SupportedLanguage } from "../locales";

type FaqItem = {
  question: string;
  answer: string;
};

type CaptionsLandingPopupContent = {
  title: string;
  description: string;
  emailLabel: string;
  emailPlaceholder: string;
  benefits: string[];
  cancel: string;
  submit: string;
  legalTextBeforeLink: string;
};

type CaptionsLandingSuccessContent = {
  title: string;
  message: string;
  checkInbox: string;
  whatsNext: string;
  checkEmail: string;
  checkSpam: string;
  useSubtitles: string;
  processAnother: string;
  close: string;
};

type CaptionsLandingWorkflowContent = {
  createSubtitles: string;
  uploadVideoForSubtitles: string;
  selectTranslationLanguage: string;
  needsTranslation: string;
  translationLanguage: string;
  fixValidationError: string;
};

export type CaptionsLandingContent = {
  navLabel: string;
  footerLabel: string;
  slug: string;
  heroTitle: string;
  heroDescription: string;
  seoTitle: string;
  seoDescription: string;
  keywords: string;
  faqHeadingTitle: string;
  faqHeadingSubtitle: string;
  workflow: CaptionsLandingWorkflowContent;
  popup: CaptionsLandingPopupContent;
  success: CaptionsLandingSuccessContent;
  faq: FaqItem[];
};

const captionsLandingContentByLanguage = captionsLandingData as Record<
  SupportedLanguage,
  CaptionsLandingContent
>;

export const FREE_CAPTIONS_LANDING_PATH = `/${captionsLandingContentByLanguage.en.slug}` as const;

export const FREE_CAPTIONS_LANDING_PATHS_BY_LANGUAGE = Object.fromEntries(
  Object.entries(captionsLandingContentByLanguage).map(([language, entry]) => [
    language,
    language === "en" ? `/${entry.slug}` : `/${language}/${entry.slug}`,
  ]),
) as Record<SupportedLanguage, string>;

export function getCaptionsLandingContent(language: SupportedLanguage): CaptionsLandingContent {
  return captionsLandingContentByLanguage[language] || captionsLandingContentByLanguage.en;
}

export function getCaptionsLandingFaq(language: SupportedLanguage): FaqItem[] {
  return getCaptionsLandingContent(language).faq;
}

export function getCaptionsLandingSeo(language: SupportedLanguage): CaptionsLandingContent {
  return getCaptionsLandingContent(language);
}

export function getCaptionsLandingNavLabel(language: SupportedLanguage): string {
  return getCaptionsLandingContent(language).navLabel;
}

export function getCaptionsLandingFooterLabel(language: SupportedLanguage): string {
  return getCaptionsLandingContent(language).footerLabel;
}

export function getCaptionsLandingPath(language: SupportedLanguage): string {
  return (
    FREE_CAPTIONS_LANDING_PATHS_BY_LANGUAGE[language] ||
    FREE_CAPTIONS_LANDING_PATHS_BY_LANGUAGE.en
  );
}
